import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import type { SavedLyric, LyricInput, LyricResult } from '@creanizer/types'
import { createIndexedDBStorage } from '@creanizer/utils'

interface LyricStore {
  lyrics: SavedLyric[]
  addLyric: (lyric: Omit<SavedLyric, 'id' | 'createdAt'>) => void
  updateLyric: (id: string, updates: Partial<SavedLyric>) => void
  deleteLyric: (id: string) => void
  clearLyrics: () => void
  toggleFavorite: (id: string) => void
  exportLyrics: () => string
  importLyrics: (jsonString: string) => void
  searchLyrics: (query: string) => SavedLyric[]
  getLyric: (id: string) => SavedLyric | undefined
  getLyricsByProject: (projectId: string) => SavedLyric[]
  addTag: (id: string, tag: string) => void
  removeTag: (id: string, tag: string) => void
}

const MAX_LYRICS = 200

export const useLyricStore = create<LyricStore>()(
  persist(
    (set, get) => ({
      lyrics: [],
      
      addLyric: (lyric) => {
        const newLyric: SavedLyric = {
          ...lyric,
          id: `${Date.now()}_${Math.random().toString(36).slice(2, 11)}`,
          createdAt: new Date().toISOString()
        }
        
        set(state => {
          const lyrics = [newLyric, ...state.lyrics]
          // 最大保存数制限
          if (lyrics.length > MAX_LYRICS) {
            lyrics.pop()
          }
          return { lyrics }
        })
      },
      
      updateLyric: (id, updates) => {
        set(state => ({
          lyrics: state.lyrics.map(lyric =>
            lyric.id === id
              ? { ...lyric, ...updates, updatedAt: new Date().toISOString() }
              : lyric
          )
        }))
      },
      
      deleteLyric: (id) => {
        set(state => ({
          lyrics: state.lyrics.filter(lyric => lyric.id !== id)
        }))
      },
      
      clearLyrics: () => {
        set({ lyrics: [] })
      },
      
      toggleFavorite: (id) => {
        set(state => ({
          lyrics: state.lyrics.map(lyric =>
            lyric.id === id
              ? { ...lyric, isFavorite: !lyric.isFavorite }
              : lyric
          )
        }))
      },
      
      exportLyrics: () => {
        const { lyrics } = get()
        return JSON.stringify(lyrics, null, 2)
      },
      
      importLyrics: (jsonString) => {
        try {
          const imported = JSON.parse(jsonString)
          if (Array.isArray(imported)) {
            // 既存のIDと重複しないように新しいIDを生成
            const importedLyrics = imported.map((lyric: SavedLyric) => ({
              ...lyric,
              id: `${Date.now()}_${Math.random().toString(36).slice(2, 11)}`,
              createdAt: lyric.createdAt || new Date().toISOString(),
              updatedAt: new Date().toISOString()
            }))
            
            set(state => {
              let lyrics = [...importedLyrics, ...state.lyrics]
              // 最大保存数制限
              if (lyrics.length > MAX_LYRICS) {
                lyrics = lyrics.slice(0, MAX_LYRICS)
              }
              return { lyrics }
            })
          }
        } catch (error) {
          console.error('Failed to import lyrics:', error)
          throw new Error('インポートに失敗しました。JSONファイルを確認してください。')
        }
      },
      
      searchLyrics: (query) => {
        const { lyrics } = get()
        const searchTerm = query.toLowerCase()
        
        return lyrics.filter(lyric => {
          // タイトルで検索
          if (lyric.result.basicInfo.title.toLowerCase().includes(searchTerm)) {
            return true
          }
          // ジャンルで検索
          if (lyric.result.basicInfo.genre.toLowerCase().includes(searchTerm)) {
            return true
          }
          // テーマで検索
          if (lyric.result.basicInfo.theme.toLowerCase().includes(searchTerm)) {
            return true
          }
          // タグで検索
          if (lyric.tags?.some(tag => tag.toLowerCase().includes(searchTerm))) {
            return true
          }
          // クイックプロンプトで検索
          if (lyric.input.quickPrompt?.toLowerCase().includes(searchTerm)) {
            return true
          }
          // 歌詞本文で検索
          if (lyric.result.content.fullLyric.toLowerCase().includes(searchTerm)) {
            return true
          }
          return false
        })
      },
      
      getLyric: (id) => {
        const { lyrics } = get()
        return lyrics.find(lyric => lyric.id === id)
      },
      
      getLyricsByProject: (projectId) => {
        const { lyrics } = get()
        return lyrics.filter(lyric => lyric.projectId === projectId)
      },
      
      addTag: (id, tag) => {
        set(state => ({
          lyrics: state.lyrics.map(lyric =>
            lyric.id === id
              ? { 
                  ...lyric, 
                  tags: [...(lyric.tags || []), tag],
                  updatedAt: new Date().toISOString()
                }
              : lyric
          )
        }))
      },
      
      removeTag: (id, tag) => {
        set(state => ({
          lyrics: state.lyrics.map(lyric =>
            lyric.id === id
              ? { 
                  ...lyric, 
                  tags: lyric.tags?.filter(t => t !== tag) || [],
                  updatedAt: new Date().toISOString()
                }
              : lyric
          )
        }))
      }
    }),
    {
      name: 'lyric-storage',
      storage: createIndexedDBStorage('lyric-storage'),
      version: 1,
      partialize: (state) => ({
        lyrics: state.lyrics
      })
    }
  )
)